import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useBackgroundNavigate } from '@shared/hooks'
import { useDebounceEffect } from '@shared/hooks/use-debounce-effect'
import { useSearchParams } from '@shared/hooks/use-search-params'
import { DateField } from '@shared/modules/form/fields/date'
import { RadioField } from '@shared/modules/form/fields/radio'
import { SelectField } from '@shared/modules/form/fields/select'
import { TextField } from '@shared/modules/form/fields/text'
import { FormAlert } from '@shared/modules/form/styled'
import { FormOptions, FormType } from '@shared/modules/form/types'
import { localURLMaker } from '@shared/utils/url-maker'
import { Button, Col, Form, Radio, Row, Select, Spin, Typography } from 'antd'
import { Formik, FormikProps } from 'formik'

import { CustomersService } from '../../customers/services'
import { Customer, CustomerFormData } from '../../customers/types'
import { useGetRegions } from '../../regions/hooks'
import { useStatusList } from '../../status/hooks/use-list'
import { AppointmentRoutes } from '../constants'
import { useAppointmentCustomerForm } from '../hooks/use-customer-form'

type FormProps = FormikProps<CustomerFormData> & { type: FormType }

const FormikComponent = memo<FormProps>(({ isSubmitting, handleSubmit, status, type, setFieldValue, values }) => {
  const [similarUsersCount, setSimilarUsersCount] = useState<number | null>(0)
  const [similarCustomerId, setSimilarCustomerId] = useState<number | null>(null)
  const [similarCustomers, setSimilarCustomers] = useState<Customer[]>([])
  const { searchParams, remove: removeSearchParams } = useSearchParams<{ passportSecret?: string }>()
  const navigate = useBackgroundNavigate()

  const regions = useGetRegions()

  useEffect(() => {
    if (searchParams.passportSecret) {
      setTimeout(() => {
        setFieldValue('passportSecret', searchParams.passportSecret)
        removeSearchParams.current('passportSecret')
      }, 10)
    }
  }, [removeSearchParams, searchParams.passportSecret, setFieldValue])

  const statusCollection = useStatusList({ modelId: 7 })

  const gendersNode = useMemo(
    () => [
      <Radio key='male' value='male'>
        Kişi
      </Radio>,
      <Radio key='female' value='female'>
        Qadın
      </Radio>,
    ],
    [],
  )

  const statusOptions = useMemo(
    () =>
      statusCollection.data?.map((status) => (
        <Select.Option key={status.id} value={status.id.toString()}>
          {status.name}
        </Select.Option>
      )),
    [statusCollection.data],
  )

  useDebounceEffect(
    useCallback(async () => {
      if (!!values.firstname || !!values.lastname || !!values.patronymic) {
        const result = await CustomersService.getAll({
          name: values.firstname,
          surname: values.lastname,
          patronymic: values.patronymic,
          page: 1,
          per_page: 5000,
        })

        if (result.status !== 200) {
          return
        }

        setSimilarUsersCount(result.meta.total)
        setSimilarCustomers(result.data)

        if (result.data.length === 1) {
          setSimilarCustomerId(result.data[0].id)
        } else {
          setSimilarCustomerId(null)
        }
      } else {
        setSimilarUsersCount(null)
      }
    }, [values.firstname, values.lastname, values.patronymic]),
    1000,
  )

  useEffect(() => {
    localStorage.setItem('franchise_value', values.franchise)
  }, [values.franchise])

  const redirectToSimilarUser = useCallback(() => {
    if (!similarCustomerId) {
      return
    }

    navigate(localURLMaker(AppointmentRoutes.CREATE_BY_USER_ID, { userId: similarCustomerId }))
  }, [navigate, similarCustomerId])

  const showSimilarCustomers = useCallback(() => {
    navigate('/similar-users', { withBackground: true, state: { customers: similarCustomers } })
  }, [navigate, similarCustomers])

  return (
    <Form layout='vertical'>
      <FormAlert $visible={!!status} message={status?.message} type={status?.type} />
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <TextField name='firstname' item={{ label: 'Ad' }} input={{ disabled: type === 'read' }} />
        </Col>
        <Col xs={24} md={12}>
          <TextField name='lastname' item={{ label: 'Soyad' }} input={{ disabled: type === 'read' }} />
        </Col>
        <Col xs={24} md={12}>
          <TextField name='patronymic' item={{ label: 'Ata adı' }} input={{ disabled: type === 'read' }} />
        </Col>
        <Col xs={24} md={12}>
          <SelectField name='nationality' item={{ label: 'Milliyət' }} input={{ disabled: type === 'read' }}>
            <Select.Option value='Azərbaycanlı'>Azərbaycanlı</Select.Option>
            <Select.Option value='Türk'>Türk</Select.Option>
            <Select.Option value='Rus'>Rus</Select.Option>
            <Select.Option value='Gürcü'>Gürcü</Select.Option>
            <Select.Option value='Digər'>Digər</Select.Option>
          </SelectField>
        </Col>
        <Col xs={24} md={12}>
          <TextField name='passportSecret' item={{ label: 'FİN Kod' }} input={{ disabled: type === 'read' }} />
        </Col>
        <Col xs={24} md={12}>
          <TextField name='phoneNumber' item={{ label: 'Telefon nömrəsi' }} input={{ disabled: type === 'read', addonBefore: '+994' }} />
        </Col>
        <Col xs={24} md={12}>
          <DateField name='birthDate' item={{ label: 'Təvəllüd' }} input={{ disabled: type === 'read' }} />
        </Col>
        <Col xs={24} md={12}>
          <TextField name='email' item={{ label: 'Email' }} input={{ disabled: type === 'read' }} />
        </Col>
        <Col xs={24} md={12}>
          <SelectField name='statusId' item={{ label: 'Status' }} input={{ loading: statusCollection.loading, disabled: type === 'read' }}>
            {statusOptions}
          </SelectField>
        </Col>
        <Col xs={24} md={12}>
          <RadioField name='gender' item={{ label: 'Cins' }} input={{ disabled: type === 'read' }}>
            {gendersNode}
          </RadioField>
        </Col>
        <Col xs={24} md={type === 'read' ? 9 : 12}>
          <TextField name='insuranceNumber' format='decimal' item={{ label: 'Sığorta nömrəsi' }} />
        </Col>
        <Col xs={24} md={type === 'read' ? 9 : 12}>
          <TextField name='franchise' format='decimal' item={{ label: 'Franşiza' }} input={{ addonAfter: '%' }} />
        </Col>
        <Col xs={24} md={type === 'read' ? 9 : 12}>
          <SelectField name='regionId' item={{ label: 'Yaşadığı ərazi' }} input={{ placeholder: 'Yaşadığı ərazi seçin...' }}>
            {regions.data?.map((elem) => (
              <Select.Option value={elem.id.toString()} key={elem.id}>
                {elem.name}
              </Select.Option>
            ))}
          </SelectField>
        </Col>
        {type === 'read' && (
          <Col xs={24} md={6} style={{ marginTop: '30px' }}>
            <Button onClick={() => handleSubmit()} type='primary' loading={isSubmitting} block={true}>
              Düzəliş et
            </Button>
          </Col>
        )}
        {type !== 'read' && similarUsersCount !== null && (
          <Col xs={24}>
            <div style={{ padding: '12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {!similarUsersCount ? (
                <Typography.Paragraph type='success'>Oxşar müştəri tapılmadı</Typography.Paragraph>
              ) : (
                <Button onClick={showSimilarCustomers}>Daxil edilən məlumatlara oxşar {similarUsersCount} müştəri tapıldı</Button>
              )}
              <Button disabled={!similarCustomerId} onClick={redirectToSimilarUser} type='primary'>
                Tapılan müştəri ilə əvəzlə
              </Button>
            </div>
          </Col>
        )}
      </Row>
      {type !== 'read' && (
        <Button onClick={() => handleSubmit()} type='primary' loading={isSubmitting} block={true}>
          Təsdiqlə
        </Button>
      )}
    </Form>
  )
})


FormikComponent.displayName = 'AppointmentCustomerFormFormikComponent'

export const AppointmentCustomerForm: FC<FormOptions> = () => {
  const { initialValues, type, ...form } = useAppointmentCustomerForm()

  return (
    <Spin spinning={initialValues.loading}>
      <Formik {...form} enableReinitialize={true} initialValues={initialValues.data}>
        {(formikProps) => <FormikComponent type={type} {...formikProps} />}
      </Formik>
    </Spin>
  )
}
